import { useCallback, useMemo, useState } from "react";

import { create } from "mutative";

import { FileMetadataFileType, MyLensesLensStatus } from "../../gql-generated/types";
import { useUploadFile } from "../../lens/hooks/useUploadFile";
import { type ModalProps, useToggleState } from "../../shared/hooks";
import { type LensPreviewMediaItem } from "../../shared/state";
import { type PreviewAspectRatio, type PreviewValidationError, PreviewValidationErrors } from "../models";
import { type VideoDurationRequirement, isLegalMediaType, isVideo, validatePreviewFile } from "../validation";

export interface PreviewProps {
    status?: MyLensesLensStatus;
    previewMediaItems: LensPreviewMediaItem[];
    setPreviewMediaItems: (items: LensPreviewMediaItem[]) => void;
    videoDurationRequirement?: VideoDurationRequirement;
    videoOnly?: boolean;
}

export interface LensPreviewStateHook {
    videoOnly?: boolean;
    modalProps: ModalProps;
    previewMediaItems: LensPreviewMediaItem[];
    validationMessage: string | null;
    isTouched: boolean;
    previewInReview: boolean;
    uploadFileLoading: boolean;
    uploadFile: (file: File, idx: number, aspectRatio?: PreviewAspectRatio) => Promise<void>;
    removePreview: (idx: number) => void;
    movePreview: (oldIdx: number, newIdx: number) => void;
    finalizeChanges: () => void;
    showModal: () => void;
}

export const useLensPreviewState = ({
    status,
    previewMediaItems,
    setPreviewMediaItems,
    videoDurationRequirement,
    videoOnly = false,
}: PreviewProps): LensPreviewStateHook => {
    const [modalProps, showModal] = useToggleState();
    const [validationMessage, setValidationMessage] = useState<string | null>(null);
    const [isTouched, setIsTouched] = useState(false);

    const [initialItemsOnModalOpen, setInitialItemsOnModalOpen] = useState<LensPreviewMediaItem[]>([]);
    const [workingItems, setWorkingItems] = useState<LensPreviewMediaItem[]>([]);

    const { uploadFile: uploadFileGql, loading: uploadFileLoading } = useUploadFile();

    const captureInitialStateAndOpenModal = useCallback(() => {
        setInitialItemsOnModalOpen([...previewMediaItems]);
        setWorkingItems([...previewMediaItems]);
        showModal();
    }, [previewMediaItems, showModal]);

    const uploadFile = useCallback(
        async (file: File, idx: number, aspectRatio?: PreviewAspectRatio): Promise<void> => {
            setIsTouched(true);
            setValidationMessage(null);
            const url = URL.createObjectURL(file);
            try {
                await validatePreviewFile({
                    file,
                    url,
                    fileTypeValidator: videoOnly ? isVideo : isLegalMediaType,
                    formatErrorMsg: videoOnly
                        ? PreviewValidationErrors.IncorrectVideoFormat
                        : PreviewValidationErrors.IncorrectFormat,
                    aspectRatio,
                    videoDurationRequirement,
                });
                const result = await uploadFileGql({
                    variables: {
                        file,
                        fileType: FileMetadataFileType.FileTypePreviewMedia,
                    },
                });

                if (result.data) {
                    const referenceId = result.data.uploadFile.uploadRefId;
                    if (referenceId) {
                        const updatedItems = create(workingItems, (draft) => {
                            // No empty previews inbetween allowed
                            if (idx > draft.length) {
                                idx = draft.length;
                            }
                            draft[idx] = {
                                file,
                                url,
                                type: "reference",
                                name: file.name,
                                isVideo: isVideo(file.type),
                                referenceId,
                            };
                        });
                        setWorkingItems(updatedItems);
                    }
                }
            } catch (error) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- error type
                setValidationMessage((error as PreviewValidationError).message);
                throw error;
            }
        },
        [videoOnly, videoDurationRequirement, workingItems, uploadFileGql],
    );

    const removePreview = useCallback(
        (idx: number) => {
            setIsTouched(true);
            const updatedItems = create(workingItems, (draft) => {
                idx = Math.min(idx, draft.length - 1);
                draft.splice(idx, 1);
            });
            setWorkingItems(updatedItems);
        },
        [workingItems],
    );

    const movePreview = useCallback(
        (oldIdx: number, newIdx: number) => {
            const updatedItems = create(workingItems, (draft) => {
                // Ensure indices are within bounds
                if (oldIdx < 0 || oldIdx >= draft.length || newIdx < 0 || newIdx >= draft.length) {
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- indices validated
                const itemToMove = draft[oldIdx] as LensPreviewMediaItem;
                draft.splice(oldIdx, 1);
                draft.splice(newIdx, 0, itemToMove);
            });
            setWorkingItems(updatedItems);
        },
        [workingItems],
    );

    const finalizeChanges = useCallback(() => {
        setIsTouched(true);
        setPreviewMediaItems([...workingItems]);
        modalProps.onCancel();
    }, [modalProps, workingItems, setPreviewMediaItems]);

    const previewInReview = useMemo(() => status === MyLensesLensStatus.LensStatusInReview, [status]);

    const closeModal = useCallback(() => {
        setPreviewMediaItems([...initialItemsOnModalOpen]);
        setValidationMessage(null);
        modalProps.onCancel();
    }, [modalProps, initialItemsOnModalOpen, setPreviewMediaItems]);

    return {
        videoOnly,
        modalProps: {
            ...modalProps,
            onCancel: closeModal,
        },
        uploadFileLoading,
        previewMediaItems: modalProps.visible ? workingItems : previewMediaItems,
        validationMessage,
        isTouched,
        previewInReview,
        uploadFile,
        removePreview,
        movePreview,
        finalizeChanges,
        showModal: captureInitialStateAndOpenModal,
    };
};
